import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import infoImg from "../../assets/images/info.svg";
import profileImg from "../../assets/images/profile.svg";
import notification from "../../assets/images/notification.svg";
import coins from "../../assets/images/coins.svg";
import { getCurrentUserProfile } from "../../services/dashboardService";
import { requestPayout } from "../../services/payoutService";
import { convertCredits, CREDITS_PER_DOLLAR } from "../../services/creditsService";
import './rewards.scss';

function Rewards() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [userAvatar, setUserAvatar] = useState(profileImg);
  const [cashEarned, setCashEarned] = useState(0);
  const [creditsEarned, setCreditsEarned] = useState(0);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutError, setPayoutError] = useState('');
  const [payoutSuccess, setPayoutSuccess] = useState(false);
  const [convertCreditsAmount, setConvertCreditsAmount] = useState('');
  const [convertError, setConvertError] = useState('');
  const [convertSuccess, setConvertSuccess] = useState('');
  const [converting, setConverting] = useState(false);

  const maxConvertibleCredits = Math.floor(Number(creditsEarned || 0) / CREDITS_PER_DOLLAR) * CREDITS_PER_DOLLAR;
  const convertPreviewCash = convertCreditsAmount
    ? (parseInt(convertCreditsAmount, 10) / CREDITS_PER_DOLLAR)
    : 0;

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const profile = await getCurrentUserProfile();
        if (profile) {
          setUserProfile(profile);
          if (profile.avatar && profile.avatar !== 'Profile') {
            setUserAvatar(profile.avatar);
          } else {
            setUserAvatar(profileImg);
          }
          setCashEarned(profile.cashEarned || 0);
          setCreditsEarned(profile.creditsEarned || 0);
        }
      } catch (error) {
        // console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, []);

  const refreshBalances = async () => {
    const updatedProfile = await getCurrentUserProfile();
    if (updatedProfile) {
      setCashEarned(updatedProfile.cashEarned || 0);
      setCreditsEarned(updatedProfile.creditsEarned || 0);
      setUserProfile(updatedProfile);
    }
  };

  const handlePayoutRequest = async () => {
    const amount = parseFloat(payoutAmount);
    
    // Validation
    if (!amount || amount <= 0) {
      setPayoutError('Please enter a valid amount');
      return;
    }
    
    if (amount < 20) {
      setPayoutError('Minimum withdrawal amount is $20.00');
      return;
    }
    
    if (amount > cashEarned) {
      setPayoutError('Amount cannot exceed available balance');
      return;
    }

    try {
      setLoading(true);
      setPayoutError('');
      
      const response = await requestPayout({
        amount: amount,
        currency: 'USD'
      });

      if (response.success) {
        setPayoutSuccess(true);
        setPayoutAmount('');
        await refreshBalances();
      } else {
        setPayoutError(response.message || 'Failed to process payout request');
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setPayoutError(apiMessage || 'Failed to process payout request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertCredits = async () => {
    const credits = parseInt(convertCreditsAmount, 10);

    if (!credits || credits <= 0) {
      setConvertError('Enter how many credits to convert');
      return;
    }

    if (credits % CREDITS_PER_DOLLAR !== 0) {
      setConvertError(`Credits must be a multiple of ${CREDITS_PER_DOLLAR}`);
      return;
    }

    if (credits > creditsEarned) {
      setConvertError('Amount cannot exceed your credit balance');
      return;
    }

    try {
      setConverting(true);
      setConvertError('');
      setConvertSuccess('');

      const response = await convertCredits(credits);
      if (response?.success) {
        const cash = response.converted?.cash ?? (credits / CREDITS_PER_DOLLAR);
        setConvertSuccess(`Converted ${credits} credits to $${Number(cash).toFixed(2)}`);
        setConvertCreditsAmount('');
        if (response.balances) {
          setCreditsEarned(response.balances.creditsEarned || 0);
          setCashEarned(response.balances.cashEarned || 0);
        } else {
          await refreshBalances();
        }
      } else {
        setConvertError(response?.message || 'Failed to convert credits');
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setConvertError(apiMessage || 'Failed to convert credits. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const handleConvertMax = () => {
    if (maxConvertibleCredits >= CREDITS_PER_DOLLAR) {
      setConvertCreditsAmount(String(maxConvertibleCredits));
      setConvertError('');
      setConvertSuccess('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('username');
    localStorage.removeItem('immerselyEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('userData');
    navigate('/');
  };

  return (
    <div className="modern-dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-inner">
            <div className="logo-section" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
              <img src={infoImg} alt="Immersely Logo" className="logo-img" />
              <span className="logo-text">IMMERSELY</span>
              <div className="portal-badge">Rewards</div>
            </div>

            <div className="header-actions">
              <button className="header-btn" onClick={() => navigate('/dashboard')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9,22 9,12 15,12 15,22"></polyline>
                </svg>
              </button>
              <button className="header-btn">
                <img src={notification} alt="notifications" />
              </button>
              <div className="profile-dropdown">
                <button className="header-btn profile-btn">
                  <img src={userAvatar || profileImg} alt="profile" onError={(e) => { e.target.src = profileImg; }} />
                </button>
                <div className="dropdown-menu">
                  <div className="dropdown-item" onClick={() => navigate('/dashboard')}>Dashboard</div>
                  <div className="dropdown-item" onClick={() => navigate('/settings')}>Settings</div>
                  <div className="dropdown-item" onClick={handleLogout}>Logout</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="rewards-container">
        <div className="rewards-header">
          <h1>Rewards & Payouts</h1>
          <p>Manage your earnings, convert credits, and request payouts</p>
        </div>

        <div className="rewards-grid">
          {/* Cash Earnings Card */}
          <div className="rewards-card cash-card">
            <div className="card-header">
              <div className="card-icon cash-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
              </div>
              <h2>Cash Earned</h2>
            </div>
            <div className="card-content">
              <div className="balance-display">
                <span className="balance-amount">${parseFloat(cashEarned || 0).toFixed(2)}</span>
                <span className="balance-label">Available for withdrawal</span>
              </div>
              
              {cashEarned >= 20 ? (
                <div className="payout-section">
                  <h3>Request Payout</h3>
                  <div className="payout-form">
                    <div className="form-group">
                      <label>Amount (USD)</label>
                      <div className="input-group">
                        <span className="input-prefix">$</span>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="20.00"
                          min="20"
                          max={cashEarned}
                          step="0.01"
                          value={payoutAmount}
                          onChange={(e) => {
                            setPayoutAmount(e.target.value);
                            setPayoutError('');
                            setPayoutSuccess(false);
                          }}
                          disabled={loading}
                        />
                      </div>
                      <span className="helper-text">Minimum: $20.00 | Maximum: ${parseFloat(cashEarned).toFixed(2)}</span>
                    </div>

                    {payoutError && (
                      <div className="error-message">{payoutError}</div>
                    )}

                    {payoutSuccess && (
                      <div className="success-message">
                        Payout request submitted successfully! You will receive payment within 3-5 business days.
                      </div>
                    )}

                    <button
                      className="btn btn-primary btn-payout"
                      onClick={handlePayoutRequest}
                      disabled={loading || !payoutAmount || parseFloat(payoutAmount) < 20}
                    >
                      {loading ? 'Processing...' : 'Request Payout'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="payout-info">
                  <p>You need at least $20.00 to request a payout.</p>
                  <p className="info-text">Keep playtesting or convert credits to earn more!</p>
                </div>
              )}
            </div>
          </div>

          {/* Credits Card */}
          <div className="rewards-card credits-card">
            <div className="card-header">
              <div className="card-icon credits-icon">
                <img src={coins} alt="coins" style={{ width: '32px', height: '32px' }} />
              </div>
              <h2>Credits</h2>
            </div>
            <div className="card-content">
              <div className="balance-display">
                <span className="balance-amount credits-amount">{creditsEarned || 0}</span>
                <span className="balance-label">Available credits</span>
              </div>

              <div className="conversion-rate">
                Conversion rate: <strong>{CREDITS_PER_DOLLAR} credits = $1.00</strong>
              </div>

              {maxConvertibleCredits >= CREDITS_PER_DOLLAR ? (
                <div className="payout-section convert-section">
                  <h3>Convert to Cash</h3>
                  <div className="payout-form">
                    <div className="form-group">
                      <label>Credits to convert</label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control convert-input"
                          placeholder={String(CREDITS_PER_DOLLAR)}
                          min={CREDITS_PER_DOLLAR}
                          max={maxConvertibleCredits}
                          step={CREDITS_PER_DOLLAR}
                          value={convertCreditsAmount}
                          onChange={(e) => {
                            setConvertCreditsAmount(e.target.value);
                            setConvertError('');
                            setConvertSuccess('');
                          }}
                          disabled={converting}
                        />
                      </div>
                      <span className="helper-text">
                        Multiples of {CREDITS_PER_DOLLAR} only · Max: {maxConvertibleCredits}
                        {convertCreditsAmount && !Number.isNaN(convertPreviewCash) ? (
                          <> · ≈ ${Number(convertPreviewCash).toFixed(2)}</>
                        ) : null}
                      </span>
                    </div>

                    {convertError && (
                      <div className="error-message">{convertError}</div>
                    )}

                    {convertSuccess && (
                      <div className="success-message">{convertSuccess}</div>
                    )}

                    <div className="convert-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleConvertMax}
                        disabled={converting}
                      >
                        Convert Max
                      </button>
                      <button
                        className="btn btn-primary btn-payout"
                        onClick={handleConvertCredits}
                        disabled={
                          converting ||
                          !convertCreditsAmount ||
                          parseInt(convertCreditsAmount, 10) < CREDITS_PER_DOLLAR
                        }
                      >
                        {converting ? 'Converting...' : 'Convert to Cash'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="payout-info">
                  <p>You need at least {CREDITS_PER_DOLLAR} credits to convert.</p>
                  <p className="info-text">Earn credits from public playtests and feedback.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payout Information */}
        <div className="payout-info-section">
          <h3>Payout Information</h3>
          <ul>
            <li>Convert credits anytime at {CREDITS_PER_DOLLAR} credits = $1.00</li>
            <li>Converted cash is added to your Cash Earned balance</li>
            <li>Minimum withdrawal amount is $20.00</li>
            <li>Payouts are processed within 3-5 business days</li>
            <li>Make sure your payout method is set up in <a href="/settings" onClick={(e) => { e.preventDefault(); navigate('/settings'); }}>Settings</a></li>
            <li>Wise transfers typically arrive within 1-2 business days</li>
            <li>PayPal transfers are usually instant</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Rewards;
